
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { uploadSectionImage } from "@/services/landingPage/landingPageService";
import { toast } from "@/hooks/use-toast";

interface ImageUploaderProps {
  currentUrl?: string;
  onImageUpload: (url: string) => void;
  section: string;
  label: string;
  placeholder?: string;
}

export function ImageUploader({ 
  currentUrl, 
  onImageUpload, 
  section, 
  label, 
  placeholder 
}: ImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 10MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const url = await uploadSectionImage(file, section);
      onImageUpload(url);
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      
      {/* URL Input */}
      <Input
        value={currentUrl || ""}
        onChange={(e) => onImageUpload(e.target.value)}
        placeholder={placeholder || "Enter image URL or upload below"}
      />

      {/* Image Preview */}
      {currentUrl && (
        <div className="relative inline-block">
          <img 
            src={currentUrl} 
            alt="Preview" 
            className="w-full max-w-xs h-32 object-cover rounded border"
          />
          <Button
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
            onClick={() => onImageUpload("")}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? "border-primary bg-primary/5" 
            : "border-gray-300 hover:border-gray-400"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id={`file-upload-${section}`}
          disabled={isUploading}
        />
        
        <label 
          htmlFor={`file-upload-${section}`} 
          className="cursor-pointer flex flex-col items-center space-y-2"
        >
          <div className="p-3 bg-gray-100 rounded-full">
            {isUploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            ) : (
              <ImageIcon className="h-6 w-6 text-gray-600" />
            )}
          </div>
          <div className="text-sm text-gray-600">
            {isUploading ? (
              "Uploading..."
            ) : (
              <>
                <span className="font-medium text-primary">Click to upload</span>
                {" or drag and drop"}
              </>
            )}
          </div>
          <div className="text-xs text-gray-400">
            PNG, JPG, WebP up to 10MB
          </div>
        </label>
      </div>
    </div>
  );
}
